<template>
  <div class="flex flex-col">
    <div
      class="rounded-lg border border-solid bg-white border-surface-200 shadow-sm px-3 focus-within:border-primary-500 hover:border-surface-400 focus-within:hover:border-primary-500 focus-within:ring-1 focus-within:ring-primary-300/50 focus-within:shadow-none duration-300 ease-out flex items-center gap-2"
      :class="{
        '!border-error-400 focus-within:!ring-error-300/50':
          (invalid || validationMessage) && touched,
        'h-9': size === 'sm',
        'h-11': size === 'md',
      }"
      :tabindex="-1"
      @click.stop="focusInput"
    >
      <!-- PREPEND ICON -->
      <div
        v-if="prependIcon"
        class="h-4 w-4 flex items-center justify-center shrink-0"
        :class="{
          'cursor-pointer text-surface-500 hover:text-surface-600':
            isPrependIconButton,
          'text-surface-900': !isPrependIconButton,
        }"
        @click.stop="clickPrependIcon"
      >
        <font-awesome-icon
          :icon="prependIcon"
          class="text-base"
          fixed-width
        />
      </div>

      <!-- INPUT -->
      <input
        :id="id"
        ref="inputRef"
        v-model="inputValue"
        v-bind="inputAttrs"
        :type="type"
        class="text-xs w-full font-medium outline-none placeholder:text-gray-400 disabled:bg-transparent"
        :class="{
          'text-center': centerText,
        }"
        :readonly="readonly"
        :disabled="disabled"
        :placeholder="placeholder"
        @blur.stop="emit('blur')"
      />

      <!-- APPEND ICON -->
      <div
        v-if="appendIcon || (clearable && inputValue)"
        class="h-4 w-4 flex items-center justify-center shrink-0 duration-150"
        :class="{
          'cursor-pointer text-surface-500 hover:text-surface-600':
            isAppendIconButton || clearable,
          'text-surface-400': !isAppendIconButton && !clearable,
        }"
        @click.stop="clickAppendIcon"
      >
        <font-awesome-icon
          :icon="clearable && inputValue ? 'fa-regular fa-xmark' : appendIcon"
          class="text-base"
          fixed-width
        />
      </div>

      <!-- INVALID ICON (tooltip mode only) -->
      <div
        v-if="
          validationDisplay === 'tooltip' &&
          (validationMessage || invalid) &&
          touched
        "
        ref="invalidIconRef"
        v-tooltipv2:left="validationMessage"
        class="h-4 w-4 flex items-center justify-center shrink-0 duration-150 text-error-500"
      >
        <AppIcon
          icon="fa-solid fa-circle-exclamation"
          class="text-xs"
        />
      </div>
    </div>

    <!-- BELOW VALIDATION MESSAGE (below mode only) -->
    <Transition name="below-message">
      <p
        v-if="validationDisplay === 'below' && showValidationMessage"
        class="text-xs text-error-600 pt-1.5"
      >
        {{ validationMessage }}
      </p>
    </Transition>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: 'text',
  },
  prependIcon: {
    type: String,
    required: false,
    default: undefined,
  },
  isPrependIconButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  appendIcon: {
    type: String,
    required: false,
    default: undefined,
  },
  isAppendIconButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  centerText: {
    type: Boolean,
    required: false,
    default: false,
  },
  invalid: {
    type: Boolean,
    required: false,
    default: false,
  },
  id: {
    type: String,
    required: false,
    default: undefined,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: false,
  },
  size: {
    type: String as PropType<'sm' | 'md'>,
    required: false,
    default: 'md',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  validationMessage: {
    type: String,
    required: false,
    default: undefined,
  },
  // 'tooltip' (default): red border + error icon with hover tooltip.
  // 'below': red border + message rendered under the input (no icon, no tooltip),
  // slide-fading and collapsing in/out.
  validationDisplay: {
    type: String as PropType<'tooltip' | 'below'>,
    required: false,
    default: 'tooltip',
  },
  touched: {
    type: Boolean,
    required: false,
    default: true,
  },
  placeholder: {
    type: String,
    required: false,
    default: undefined,
  },
  // Tab order of the focusable wrapper. Pass -1 to drop the wrapper as a tab
  // stop so Tab lands directly on the inner input (avoids a duplicate tab stop
  // and the focus-redirect that fires a spurious focusout on tab-in).
  tabindex: {
    type: Number,
    required: false,
    default: 0,
  },
})

const emit = defineEmits([
  'update:modelValue',
  'clickPrependIcon',
  'clickAppendIcon',
  'blur',
])

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const showValidationMessage = computed(
  () => !!props.validationMessage && props.touched,
)

// class attr is unused
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { class: unusedFieldClass, id: unusedFieldId, ...inputAttrs } = useAttrs()

const inputRef = ref<HTMLInputElement | null>(null)

function focusInput() {
  if (props.disabled) return
  inputRef.value?.focus()
}

function clickPrependIcon() {
  emit('clickPrependIcon')
}

function clickAppendIcon() {
  emit('clickAppendIcon')

  if (props.clearable) {
    inputValue.value = ''
  }
}

// Google-Drive-style rename affordance: focus the field with its full value
// selected so typing replaces it wholesale.
function selectInput() {
  if (props.disabled) return
  inputRef.value?.focus()
  inputRef.value?.select()
}

defineExpose({
  focus: focusInput,
  select: selectInput,
  /**
   * The element itself, for a parent that has to correct what is on screen
   * when the model did not move. Clamping is the case: typing "500" into a
   * field capped at 50 emits 50 when the model already holds 50, so nothing
   * re-renders and the rejected text would stay in the box.
   */
  input: inputRef,
})

const invalidIconRef = useTemplateRef('invalidIconRef')
</script>

<style scoped>
/* Below-mode validation message: fade + slide, ease-out on enter, ease-in on leave.
   <Transition> keeps the leaving element (and its text) mounted through the leave,
   so the message stays visible while it fades out. */
.below-message-enter-active {
  transition:
    opacity 0.3s ease-out,
    transform 0.3s ease-out;
}

.below-message-leave-active {
  transition:
    opacity 0.3s ease-in,
    transform 0.3s ease-in;
}

.below-message-enter-from,
.below-message-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}
</style>
